import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { IntegrationCatalog } from "@modules/erpIntegrations/types";
import { useEditConfigMutation } from "@modules/erpIntegrations/services/mutations";
import { listConfigsQueryKey } from "@modules/erpIntegrations/services/queries";
import { applyConfigApiErrors } from "@modules/erpIntegrations/utils";
import {
  ConfigFormValues,
  configSchema,
} from "@modules/erpIntegrations/validations";

export function useEditConfigModal(
  modalRef: RefObject<ModalRef>,
  integrations: IntegrationCatalog[],
  onComplete?: () => void
) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<ConfigFormValues>({
    resolver: zodResolver(configSchema(integrations)),
    mode: "onBlur",
    defaultValues: {
      active: true,
      credentials: {},
      test_document: "",
      access_token: "",
      plan_map_option: null,
    },
  });

  const { mutateAsync, isPending } = useEditConfigMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConfigsQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("v1/agreement_integration_config_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
    onError: (error) => {
      applyConfigApiErrors(error, methods.setError);
    },
  });

  const handleSubmit = async (data: ConfigFormValues) => {
    const configId = modalRef.current?.getId();
    const allowed = new Set(
      integrations
        .find((item) => item.id === Number(data.integration))
        ?.credential_schema?.map((field) => field.key) ?? []
    );
    const credentials =
      allowed.size === 0
        ? data.credentials
        : Object.fromEntries(
            Object.entries(data.credentials ?? {}).filter(([key]) => allowed.has(key))
          );

    await mutateAsync({
      id: configId as string,
      data: { ...data, credentials },
    });
  };

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
